import styled from "@emotion/styled";
import { Navigate } from "@tanstack/react-router";
import { Flex } from "styles/utility-components";

import { BuyerActivity } from "components/buyer_activity/BuyerActivity";
import SupplierRelationships from "components/buyer_activity/supplier_relationships/SupplierRelationships";
import { generateNoticeSearchUrl } from "components/notices/utils";
import { BreadcrumbContainer, RouterBreadcrumb } from "components/ui/breadcrumb/SimpleBreadcrumb";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { useBid, useBidSupplierBuyerNoticeSummary } from "lib/hooks/api/bid/bids/useBid";
import { BuyerActivityObjectType, BuyerActivityStatus } from "lib/types/graphQLEnums";

type Props = { bidId: string };

export default function BidBuyerActivityPage({ bidId }: Props) {
  const { data: bid, isLoading, isError } = useBid({ id: bidId }, { enabled: !!bidId });

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this bid overview"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!bid) {
    return <Navigate to="/bids" />;
  }

  const noticesSearchUrl = generateNoticeSearchUrl({
    relevanceScore: [RelevanceScoreEnum.Low, RelevanceScoreEnum.Medium, RelevanceScoreEnum.High],
    buyers: bid.buyers?.map((buyer) => buyer.id) ?? [],
  });

  return (
    <Container>
      <BreadcrumbContainer>
        <RouterBreadcrumb
          overrides={{
            [`/bids/${bidId}`]: { label: bid?.name ?? "Bid details" },
            [`/bids/${bidId}/buyer-activity`]: { label: `Overview of ${bid?.name ?? "this bid"}` },
          }}
        />

        <Button buttonType="secondary" icon="share07" iconProps={{ color: "sysPrimaryDefault" }}>
          Share
        </Button>
      </BreadcrumbContainer>
      <Content>
        <Flex column gap={24}>
          <BuyerActivity
            summary={bid.buyerActivity ?? null}
            notices={bid.notice ? [bid.notice] : []}
            objectId={bidId}
            objectType={BuyerActivityObjectType.Bid}
            noticesSearchUrl={noticesSearchUrl}
            bidName={bid.name}
            buyerName={bid.notice?.buyers?.[0]?.name}
            buyerId={bid.notice?.buyers?.[0]?.guid}
            noticeId={bid.notice?.guid}
            noticeTitle={bid.notice?.name}
          />
          <SupplierRelationships
            isLoading={bid.buyerActivity?.status === BuyerActivityStatus.Pending}
            savedNotices={bid.notice ? [bid.notice] : []}
            similarNotices={bid.buyerActivity?.relatedNotices ?? []}
            incumbentSupplierIds={bid.suppliers.map((supplier) => supplier.id)}
            objectId={bid.id}
            queryProvider={useBidSupplierBuyerNoticeSummary}
            noticesSearchUrl={noticesSearchUrl}
          />
        </Flex>
      </Content>
    </Container>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
});

const Content = styled(Flex)({
  flexDirection: "column",
  gap: 48,
  padding: "48px 96px",
});
